import { Information } from "@carbon/icons-react";
import { Tooltip } from "@carbon/react";
import { FC, ReactNode } from "react";

import EmptyTableState from "src/components/DataStates/EmptyTableState.tsx";
import {
  PropertyValueRenderer,
  PropertyValueRendererParams,
} from "src/dynamic-view-renderer/Widgets/PropertyRenderer.tsx";
import { WidgetProperty } from "src/dynamic-view-renderer/Widgets/Widget.typings.ts";
import WidgetContainer, {
  WidgetContainerProps,
} from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { useT } from "src/internationalization";

import { TopLabel } from "../WidgetHeader";

export const BasicWidgetVerticalPropertyLayout: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex flex-wrap flex-col">{children}</div>
);

type BasicWidgetVerticalPropertyType = WidgetProperty &
  Pick<BasicWidgetVerticalProps, "propertyValueRendererParams"> & { isLast?: boolean };

export const BasicWidgetVerticalProperty: FC<BasicWidgetVerticalPropertyType> = ({
  value,
  label,
  isLast = false,
  comparisonValue,
  comparisonIcon,
  mismatch,
  propertyValueRendererParams,
  ...rest
}) => {
  const t = useT();
  return (
    <div
      className={`w-full flex justify-between py-5 items-center gap-3 flex-wrap ${
        !isLast ? "border-b border-solid border-borderSubtle01" : ""
      }`}
    >
      <p
        className="text-sm text-textSecondary"
        style={{ flex: comparisonValue ? "0 1 auto" : "1 1 auto" }}
      >
        {label}
      </p>
      {comparisonValue && (
        <div className="text-sm gap-3 flex items-center" style={{ flex: "0 1 auto" }}>
          {comparisonIcon && <img src={comparisonIcon} alt={comparisonIcon} />}
          {comparisonValue}
          {mismatch && (
            <Tooltip
              align="bottom-right"
              as={"p"}
              label={t("component.widget.basic.property.mismatch.tooltip")}
              className="text-sm block h-5"
            >
              <Information className="text-supportError" />
            </Tooltip>
          )}
        </div>
      )}
      <div style={{ flex: "0 1 auto" }}>
        <PropertyValueRenderer
          label={label}
          value={value}
          params={propertyValueRendererParams}
          {...rest}
        />
      </div>
    </div>
  );
};

type BasicWidgetVerticalProps = {
  properties: WidgetProperty[];
  topLabel?: TopLabel;
  ItemId?: string;
  elementCode?: string;
  propertyValueRendererParams?: PropertyValueRendererParams;
} & Omit<WidgetContainerProps, "children">;

export const BasicWidgetVertical: FC<BasicWidgetVerticalProps> = ({
  properties,
  propertyValueRendererParams,
  ...containerProps
}) => (
  <WidgetContainer {...containerProps}>
    <BasicWidgetVerticalPropertyLayout>
      {properties.map((property, index) => (
        <BasicWidgetVerticalProperty
          {...property}
          key={index}
          propertyValueRendererParams={propertyValueRendererParams}
          isLast={index === properties.length - 1}
        />
      ))}
      {properties.length === 0 && <EmptyTableState />}
    </BasicWidgetVerticalPropertyLayout>
  </WidgetContainer>
);
